import { useEffect, useState } from 'react';
import { Plant, PlantStatus, CompanionMatches } from '../../utils/Chloromance/types';
import { formatMonthList } from '../../utils/Chloromance/utils/catalogue';
import { formatPlantLocation } from '../../utils/Chloromance/utils/plants';


type PlantCardProps = {
  plant: Plant;
  status: PlantStatus;
  matches: CompanionMatches;
  justWatered: boolean;
  onWater: (plantId: string) => void;
  onEdit: (plant: Plant) => void;
  onDelete: (plant: Plant) => void;
};

export function PlantCard({ plant, status, matches, justWatered, onWater, onEdit, onDelete }: PlantCardProps) {
  const [isOpen, setIsOpen] = useState(status.state === 'Critical');

  useEffect(() => {
    if (status.state === 'Critical') setIsOpen(true);
  }, [status.state]);

  const hasCatalogueCare = Boolean(
    plant.catalogue_type || plant.suggested_location || plant.best_harvest_months?.length || plant.fruit_or_flower_months?.length
  );

  return (
    <details
      className={`plant-card plant-card-collapsible state-${status.state.toLowerCase()} ${justWatered ? 'just-watered' : ''}`}
      open={isOpen}
      onToggle={(event) => setIsOpen(event.currentTarget.open)}
      role="article"
    >
      <summary className="plant-card-summary">
        <span className="plant-card-summary-main">
          <h2>{plant.name}</h2>
          <small>{formatPlantLocation(plant.chloromance_zone)}</small>
        </span>
        <span className="plant-card-summary-status">
          <b>{status.state}</b>
          <small>{status.moisture}% · {status.nextWateringText}</small>
        </span>
        <span className="plant-card-chevron" aria-hidden="true" />
      </summary>

      <div className="plant-card-content">
        <div className="plant-name-row">
          <p className="plant-variety">{plant.variety} · {plant.plant_type || 'Other'}</p>
          {(plant.plant_count || 1) > 1 && <span className="catalogue-chip">x{plant.plant_count}</span>}
          {plant.catalogue_type && <span className="catalogue-chip">{plant.catalogue_type}</span>}
        </div>

        {(matches.companions.length > 0 || matches.antagonists.length > 0) && (
          <div className="companion-badges" aria-label={`${plant.name} companion planting matches`}>
            {matches.companions.length > 0 && (
              <span className="harmony-badge">Harmony with {matches.companions.map((match) => match.name).join(', ')}</span>
            )}
            {matches.antagonists.length > 0 && (
              <span className="conflict-badge">Location conflict with {matches.antagonists.map((match) => match.name).join(', ')}</span>
            )}
          </div>
        )}

        <div className="moisture-meter" aria-label={`${plant.name} soil moisture ${status.moisture}%`}>
          <div className="moisture-meter-label"><span>Soil Moisture</span><strong>{status.moisture}%</strong></div>
          <div className="moisture-track"><span style={{ width: `${status.moisture}%` }} /></div>
        </div>

        <dl className="plant-metrics">
          <div><dt>Plants</dt><dd>{plant.plant_count || 1}</dd></div>
          <div><dt>Frequency</dt><dd>{plant.watering_frequency_days}d</dd></div>
          <div><dt>Status</dt><dd>{status.nextWateringText}</dd></div>
        </dl>

        {hasCatalogueCare && (
          <details className="catalogue-care-details">
            <summary>Seasonal care details</summary>
            {plant.suggested_location && <p><strong>Best position:</strong> {plant.suggested_location}</p>}
            {plant.fruit_or_flower_months && plant.fruit_or_flower_months.length > 0 && (
              <p><strong>Flowers / fruit:</strong> {formatMonthList(plant.fruit_or_flower_months)}</p>
            )}
            {plant.best_harvest_months && plant.best_harvest_months.length > 0 && (
              <p><strong>Harvest:</strong> {formatMonthList(plant.best_harvest_months)}</p>
            )}
          </details>
        )}
        {plant.note && <p className="plant-note">{plant.note}</p>}
        <div className="plant-card-actions">
          <button className="watered-button" type="button" onClick={() => onWater(plant.id)}>
            Watered{justWatered && <span aria-hidden="true" />}
          </button>
          <button className="edit-plant-button" type="button" onClick={() => onEdit(plant)}>Edit</button>
          <button className="delete-plant-button" type="button" onClick={() => onDelete(plant)}>Delete</button>
        </div>
      </div>
    </details>
  );
}
